import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { cn } from "@solomon-apps/shared/src/utils/cn";
import React from "react";

/**
 * View-only affordances for the mobile client console.
 *
 * Mobile is a read-only surface (see `useViewOnly`). Two patterns share
 * the same copy voice:
 *   - `ViewOnlyHint` — a terse inline note rendered beside a disabled
 *     action ("Switch to desktop to make changes").
 *   - `DesktopOnly` — a centered placeholder that replaces a route whose
 *     only purpose is a mutation flow (add position, onboarding) and so
 *     has nothing to show in read-only mode.
 */

interface ViewOnlyHintProps {
    /** Override the default copy. */
    children?: React.ReactNode;
    className?: string;
}

/** Why a change is unavailable in the view-only console. */
export const VIEW_ONLY_REASON = "Switch to desktop to make changes.";
const DEFAULT_HINT = VIEW_ONLY_REASON;

export function ViewOnlyHint({
    children,
    className
}: ViewOnlyHintProps): React.ReactElement {
    return (
        <Text
            variant="label"
            render={<span />}
            className={cn(
                "inline-flex items-center gap-1.5 text-foreground-muted",
                className
            )}
        >
            <DesktopGlyph />
            {children ?? DEFAULT_HINT}
        </Text>
    );
}

interface DesktopOnlyProps {
    title: string;
    description: React.ReactNode;
    className?: string;
}

export function DesktopOnly({
    title,
    description,
    className
}: DesktopOnlyProps): React.ReactElement {
    return (
        <section
            aria-label="Available on desktop"
            className={cn(
                "flex flex-col items-center gap-3 px-6 py-16 text-center",
                className
            )}
        >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-surface-sunken text-foreground-muted">
                <DesktopGlyph size={20} />
            </span>
            <Text variant="h2" render={<h2 />} className="text-foreground-base">
                {title}
            </Text>
            <Text
                variant="body"
                className="max-w-[420px] text-foreground-muted"
            >
                {description}
            </Text>
        </section>
    );
}

/** Small monitor glyph shared by both affordances. */
function DesktopGlyph({ size = 14 }: { size?: number }): React.ReactElement {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="shrink-0"
        >
            <rect x="1.5" y="2.5" width="13" height="8.5" rx="1" />
            <path d="M5.5 13.5h5" />
            <path d="M8 11v2.5" />
        </svg>
    );
}
